<template>
  <div class="workflow-products-view">
    <ButtonGroup class="workflow-products-view__actions">
      <Button
        theme-color="primary"
        type="button"
        @click="
          redirect(
            `${Pages.WorkflowOverview}?mode=my&CurrentPageIndex=${currentPageIndex}`
          )
        "
        data-cy="my-work-button"
        >My Work</Button
      >
      <Button
        theme-color="primary"
        type="button"
        @click="
          redirect(
            `${Pages.WorkflowOverview}?mode=all&CurrentPageIndex=${currentPageIndex}`
          )
        "
        data-cy="all-work-button"
        >All Work</Button
      >
    </ButtonGroup>

    <div
      v-if="wfSubmission && wfSubmission.IsUrgent"
      class="workflow-products-view__urgent"
    >
      <span i>This item is marked as Urgent</span>
      <img
        class="workflow-products-view__urgent-icon"
        :src="urgentHeaderIcon"
        alt="Urgent icon"
      />
    </div>

    <UserActivityView />

    <div class="workflow-products-view__container">
      <div class="workflow-products-view__column">
        <WorkflowDashboard
          v-if="wfSubmission"
          :submission="wfSubmission"
          :workflowId="workflowId"
          @urgentStatusChanged="(u) => (wfSubmission!.IsUrgent = u)"
        />

        <WorkflowSubmitForm
          v-if="wfSubmission"
          :jobId="wfSubmission.JobID"
          :workflowId="parseInt(workflowId)"
          :status="wfSubmission.Status!"
          :privateComment="wfSubmission.LatestUserComment"
          :assignedTo="wfSubmission.AssignedTo"
          :wfSubmission="wfSubmission"
          :wfSubmissionType="EnumWorkflowType.Product"
          :companyId="wfSubmission.CompanyId"
          :companyName="wfSubmission.CompanyName"
          :attachments="workflowAttachments"
          :lineItemsStatus="wfSubmission.JobStatus"
          @formSubmitted="onFormSubmitted"
        />

        <div
          class="workflow-products-view__activity-history"
          data-cy="workflow-products-view__activity-history"
        >
          <header class="activity-history-header">Activity History</header>
          <ActivityHistoryGrid
            :activity-history="wfSubmission?.Actions ?? []"
            :attachments="workflowAttachments"
            :loading="loadingSubmission"
            :jobId="wfSubmission?.JobID ?? ''"
            :workflowId="parseInt(workflowId)"
            :submissionType="EnumWorkflowType.Product"
            :showAll="showAll"
          />
        </div>
      </div>
      <div class="workflow-products-view__column">
        <LineItemsView
          :workflowId="parseInt(workflowId)"
          :submission="wfSubmission"
          :attachments="workflowAttachments"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import urgentHeaderIcon from "@/assets/img/error-icon-15.png";
import type { WfProductSubmissionDisplay } from "@/Domain/WfProductSubmissionDisplay";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { Button, ButtonGroup } from "@progress/kendo-vue-buttons";
import { onBeforeMount, ref } from "vue";
import WorkflowDashboard from "./WorkflowDashboard.vue";
import WorkflowSubmitForm from "@/shared/ui/forms/WorkflowSubmitForm.vue";
import type { SharePointAttachment } from "@/Domain/SharePointAttachment";
import { EnumWorkflowType } from "@/Domain/EnumWorkflowType";
import ActivityHistoryGrid from "@/shared/ui/grids/ActivityHistoryGrid.vue";
import LineItemsView from "./LineItemsView.vue";
import { Pages } from "@/pages/pages";
import UserActivityView from "@/shared/ui/activity/UserActivityView.vue";
import { useActivityStore } from "@/stores/useActivityStore";

const main = useMainStore();
const activity = useActivityStore();

const urlParams = new URLSearchParams(window.location.search);
const workflowId = urlParams.get("workflowId") ?? "";
const currentPageIndex = urlParams.get("CurrentPageIndex") ?? "";
const showAll =
  urlParams.get("HistoryMode")?.toLowerCase() === "showall";
activity.setActivityId("WorkflowDetailProducts-" + workflowId);

const wfSubmission = ref<WfProductSubmissionDisplay>();
const workflowAttachments = ref<SharePointAttachment[]>([]);
const loadingSubmission = ref<boolean>(false);

const redirect = function (href: string) {
  window.location.href = href;
};

const getWfProductSubmissionAsync = async function () {
  loadingSubmission.value = true;

  await handleResponsePromiseAsync(
    api.get("VueGetWfProductSubmission", {
      id: Number(workflowId) || 0,
    }),
    (data) => {
      wfSubmission.value = data;

      // Sort actions by date
      wfSubmission.value.Actions = wfSubmission.value.Actions.sort((a, b) => {
        return (
          new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime()
        );
      });
    },
    "WorkflowDetailProductsView.vue",
    "getWfProductSubmissionAsync"
  );

  loadingSubmission.value = false;
};

const getWorkflowAttachmentsAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetWorkflowAttachments", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      jobId: wfSubmission.value!.JobID,
      workflowType: "product",
    }),
    (data) => {
      workflowAttachments.value = data;
    },
    "WorkflowDetailProductsView.vue",
    "GetWorkflowAttachmentsAsync"
  );
};

const onFormSubmitted = function () {
  getWfProductSubmissionAsync();
  getWorkflowAttachmentsAsync();
};

onBeforeMount(async () => {
  await getWfProductSubmissionAsync();
  getWorkflowAttachmentsAsync();
});
</script>

<style scoped lang="scss">
.workflow-products-view {
  margin: 8px;

  @include media-breakpoint-up(md) {
    margin: 28px;
  }

  &__urgent {
    background-color: #f8d7da;
    border-color: #f5c2c7;
    position: relative;
    padding: 1rem 1rem;
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    border-radius: 4px;
  }

  &__urgent-icon {
    vertical-align: middle;
    display: inline;
  }

  &__actions {
    display: flex;
    margin-bottom: 20px;
  }

  &__container {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    line-height: 1.5;
    font-size: 13px;

    @include media-breakpoint-up(md) {
      gap: 36px;
    }
  }

  &__column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__activity-history {
    background: white;
  }

  .activity-history-header {
    font-weight: bold;
    padding: 16px;
    font-size: 14px;
    background: #dedede;
  }
}

.k-grid-header {
  background-color: #dedede;
}

.job-info-row {
  font-size: 13px;
}

.k-chip-md {
  font-size: 12px;
}
</style>
